import {Table} from "antd";
import {ColumnProps, TablePaginationConfig} from "antd/lib/table";
import React, {FC, memo, useEffect, useState} from "react";
import moment, {Moment} from "moment";
import {
    Course,
    CourseLesson,
    GetCourseLessonsByCourseIdQuery,
    GetHolidaysByCompanyAndDateRangeQuery,
    PaymentInterval,
    TablesEnum,
    useGetCourseLessonsByCourseIdQuery,
    useGetHolidaysByCompanyAndDateRangeQuery,
} from "../../generated/graphql";
import i18n from "../../services/i18n";
import {renderAmount, renderDateAsWeekdayTag, renderDateFormatted, renderStartEndTime} from "../TableColumnRenderers/TableColumnRenderers";
import css from "./CourseLessonTable.less";
import {sortByStartDateTime} from "../../helpers/sortingFunctions";
import {QueryItem} from "../../types";
import {RowIndex} from "../../containers/courses/CourseViews/CourseLessonViews";
import {getPagination} from "../../helpers/getPagination";
import {useLocalStorageOptions} from "../../helpers/columnsLocalStorageHandler";

type CourseLessonType = Pick<CourseLesson, "id" | "startDateTime" | "endDateTime" | "locationId">;

type CourseLessonTableProps = {
    courseId: Course["id"];
    totalPrice?: number;
    paymentInterval?: PaymentInterval;
    freePlaces?: number;
    defaultPageSize?: number;
    /**
     * Late-Entry: enthaltene Lesson-IDs der Buchung (M2M). Nur wenn gesetzt und
     * nicht leer, werden nicht enthaltene Zeilen gedimmt und beschriftet —
     * ohne Prop bleibt die Tabelle im heutigen Modus (Create, Full-Course, Legacy).
     */
    includedCourseLessonIds?: ReadonlyArray<string>;
};

type GetCourseLessonsByCourseIdQueryItem = QueryItem<GetCourseLessonsByCourseIdQuery["courseLessonsByCourseId"]>;

type TableType = GetCourseLessonsByCourseIdQuery["courseLessonsByCourseId"]["0"];

const CourseLessonTable: FC<CourseLessonTableProps> = ({
    courseId,
    // courseLessons,
    totalPrice,
    paymentInterval,
    freePlaces,
    defaultPageSize = 10,
    includedCourseLessonIds,
}) => {
    const tablesEnum: TablesEnum = TablesEnum.CourseLessonTable;
    const {data, loading} = useGetCourseLessonsByCourseIdQuery({
        variables: {courseId},
    });
    const [options, setOptions] = useLocalStorageOptions({
        localStorageName: tablesEnum,
        defaultOptions: {},
        totalResults: 0,
    });
    const [holidays, setHolidays] = useState<GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"]>([]);
    const courseLessons: Array<GetCourseLessonsByCourseIdQueryItem> = data?.courseLessonsByCourseId ?? [];

    const pricePerLesson = totalPrice ? totalPrice / courseLessons.length : 0;

    // Late-Entry: nur mit nicht-leerer ID-Liste aktiv (fail-open, sonst heutiger Modus)
    const highlightExcludedLessons = Boolean(includedCourseLessonIds && includedCourseLessonIds.length > 0);
    const isExcludedLesson = (lessonId: string): boolean =>
        highlightExcludedLessons && includedCourseLessonIds!.indexOf(lessonId) === -1;

    // const pageSize = paginationConfig.pageSize ?? 10;
    // const currentPage = paginationConfig.current ?? 1;

    // HANDLE HOLIDAYS ---------------------------------------------------------------------------------

    const startAndEndDates = (): {start: string; end: string} | undefined => {
        if (courseLessons.length === 0) {
            return undefined;
        }
        const start = moment(courseLessons[0].startDateTime).startOf("day").format();
        const end = moment(courseLessons[courseLessons.length - 1].endDateTime)
            .endOf("day")
            .format();

        return {start, end};
    };

    const {data: holidayData, loading: loadingHoldiays} = useGetHolidaysByCompanyAndDateRangeQuery({
        skip: !startAndEndDates(),
        variables: {
            start: startAndEndDates()?.start,
            end: startAndEndDates()?.end,
        },
    });

    useEffect(() => {
        if (holidayData && !loadingHoldiays) {
            setHolidays(holidayData.holidaysByCompanyAndDateRange);
        }
    }, [holidayData, loadingHoldiays]);

    const getHolidayInfo = (date: Moment): GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number] | undefined => {
        const allHolidaysOfDate = holidays.filter((holiday) => {
            const holidayStart = moment(holiday.start).startOf("day");
            const holidayEnd = moment(holiday.end).endOf("day");

            return moment(date).isSameOrAfter(holidayStart) && moment(date).isSameOrBefore(holidayEnd);
        });

        const customHoliday = allHolidaysOfDate.find((holiday) => {
            return holiday.stateCode === "CUSTOM";
        });

        const holidayToShow = customHoliday ?? allHolidaysOfDate[0];

        return holidayToShow;
    };

    const renderIndex = (rowIndex: number, holiday?: GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number]) => {
        const index = rowIndex;

        return <RowIndex rowIndex={index} /* style={{...rowStyle}} */ holiday={holiday} />;
    };

    // COLUMNS ---------------------------------------------------------------------------------

    const columns: Array<ColumnProps<TableType>> = [
        {
            title: "#",
            dataIndex: "number",
            align: "center",
            render: (text, record, rowIndex) => renderIndex(rowIndex, getHolidayInfo(record.startDateTime)),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.label(),
            dataIndex: "dayOfTheWeek",
            render: (text, record) =>
                renderDateAsWeekdayTag({
                    value: record.startDateTime,
                    style: {
                        width: "100%",
                        textAlign: "center",
                        fontWeight: 600,
                    },
                }),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.label(),
            dataIndex: "startDateTime",
            render: (text, record) => (
                <>
                    {renderDateFormatted(record.startDateTime, "DD. MMM YYYY")}
                    {isExcludedLesson(record.id) && (
                        <div className={css.excludedLessonLabel}>
                            {i18n.containers.courses.AttendanceList.notIncludedInBooking()}
                        </div>
                    )}
                </>
            ),
            // defaultSortOrder: "descend",
            sorter: sortByStartDateTime,
            sortDirections: [],
            sortOrder: "ascend",
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.label(),
            dataIndex: "times",
            render: (text, record) =>
                renderStartEndTime({
                    startTime: record.startDateTime,
                    endTime: record.endDateTime,
                }),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.label(),
            dataIndex: "lessonPrice",
            render: (text, record) => (paymentInterval === PaymentInterval.PerLesson ? renderAmount(pricePerLesson) : null),
        },
        // {
        //     title: i18n.containers.courses.CourseCreate.courseStartDateTime.label(),
        //     dataIndex: "freePlaces",
        //     render: (text, record) => renderFreePlaces(freePlaces ?? 0),
        // },
    ];

    const onChange: (pagination: TablePaginationConfig) => void = (pagination) => {
        const updatedOptions = {
            ...options,
            limit: getPagination({
                pagination,
                defaultSize: defaultPageSize,
            }).limit,
            offset: getPagination({
                pagination,
                defaultSize: defaultPageSize,
            }).offset,
        };

        setOptions(updatedOptions);
    };

    return (
        <div className={css.root}>
            <Table
                loading={loading}
                showHeader={false}
                size="small"
                rowKey="id"
                columns={columns}
                dataSource={courseLessons}
                rowClassName={(record) => (isExcludedLesson(record.id) ? `${css.extraSmallRow} ${css.excludedLessonRow}` : css.extraSmallRow)}
                pagination={{
                    current: options?.offset && options?.limit ? options.offset / options.limit + 1 : 1,
                    pageSize: options?.limit ?? defaultPageSize,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "15", "20", "25", "50", "100"],
                    defaultPageSize,
                    total: courseLessons.length,
                    size: "small",
                    showTotal: (total, range) => `${range[0]}-${range[1]} von ${total}`,
                }}
                onChange={onChange}
            />
        </div>
    );
};

export default memo(CourseLessonTable);
